import _ from 'lodash'
import { isDragonBones, isFrameSequence } from '@/views/creative/utils'
import path from 'path-browserify'
import Url from 'url-parse'
import { getDmakerImage } from '@/api/creative/template'
import generateUUID from '@/views/creative/utils/generate-uuid'
import {
  convertProtocol,
  cookUpdateImageMessage,
  baseUrl,
  cookUpdateAudioMessage,
  cookUpdateVolumeAudioMessage,
  cookUpdateFrameSequenceMessage,
  encodeImageBin,
  getFontList
} from './util.ts'
import { isImage } from '@com/utils/file'
import { pickFontAndText } from '@com/i18n'

const sortDragonBonesResources = urls => {
  // source 对文件顺序有要求
  const sortedUrls = []
  urls.forEach(item => {
    const pathname = new Url(item).pathname
    if (pathname.indexOf('_tex.json') > -1) {
      sortedUrls[0] = item
    } else if (pathname.indexOf('_tex.png') > -1) {
      sortedUrls[1] = item
    } else if (pathname.indexOf('_ske.json') > -1) {
      sortedUrls[2] = item
    }
  })
  return sortedUrls
}

// function encode(val) {
//   const text = JSON.stringify([JSON.stringify(val[0])])
//   return encodeURIComponent(text)
// }
const cookUpdateDragonBonesMessage = async({
  baseUrl,
  targetResourcePath,
  newResourceUrls,
  visible
}) => {
  // [
  //   "{\"uuid\":\"HWQw6JK8wh2i281\",\"type\":3,\"animationType\":2,\"source\":[\".json\",\".png\",\".ani\"]}"
  // ]
  /**
   * generateBin
   * @param {String[]} source
   */
  const generateBin = source => {
    return [
      JSON.stringify(
        {
          uuid: generateUUID(),
          animationType: 2,
          type: 3,
          source
        }
      )
    ]
  }

  let transparentImage = ''
  // 隐藏且还没有透明图时
  if (visible === false && !transparentImage) {
    const res = await getDmakerImage(newResourceUrls.find(item => isImage(item)))
    transparentImage = _.get(res, 'data.data.url')
  }
  // source 对文件顺序有要求
  const sortedUrls = sortDragonBonesResources(newResourceUrls).map((url) => {
    if (isImage(url) && visible === false) return transparentImage
    return url
  })

  const apiUrl = `${baseUrl}?bin=${encodeImageBin(
    generateBin(sortedUrls.map(url => `${convertProtocol(url)}`))
  )}&x=.bin`
  return {
    action: 'update',
    path: targetResourcePath,
    url: apiUrl,
    type: 2
  }
}

/**
 * dispatch，分派实时预览逻辑
 * @param {any} param0
 * @param {{category: Object, resource: Object, basicResource: Object, action: String, scene: String, gameConfig: Object, i18n: Object,previewLanguage: string, visible?: Boolean, volume?: number, soundDisabled?: Boolean, mwConfig: Object, previewUrl: string}} param1
 */
export default async function livePreview(
  { commit, state, dispatch },
  {
    category,
    resource,
    basicResource,
    action,
    scene,
    gameConfig,
    i18n,
    previewLanguage,
    visible,
    volume,
    soundDisabled,
    mwConfig
  }
) {
  if (!state.gameReady) {
    console.warn('playable has not ready.')
    return
  }

  // console.log(category, resource, basicResource)
  if (!action) {
    throw new Error('action 是必传参数')
  }

  if (action === 'navigate' && !scene) {
    throw new Error('action 为 navigate 时，必须传入 scene 参数')
  }

  // 更新 gameConfig 与 i18n 都会跳转至起始阶段
  if (action === 'update') {
    // json 配置
    if (gameConfig || i18n || mwConfig) {
      const config = {
        // gameConfig: null,
        // i18n: null
      }
      // 玩法
      if (gameConfig) {
        config.gameConfig = {
          data: gameConfig
        }
      }
      // 代码字
      if (i18n) {
        const fonts = pickFontAndText(i18n.languages)
        const newFontList = await getFontList(fonts)
        config.i18n = {
          data: i18n,
          language: previewLanguage || i18n.fallbackLocale,
          fontList: newFontList
        }
      }
      // mwConfig
      if (mwConfig) {
        config.mwConfig = {
          data: mwConfig
        }
      }
      dispatch('postMessage', {
        action: 'update',
        type: 99,
        config,
        scene: 'start'
      })
      return
    }

    // 当 action 是 update 时，且没有 gameConfig 和 i18n，需要校验必须的参数
    if (_.isEmpty(gameConfig) && _.isEmpty(i18n)) {
      if (_.isEmpty(resource)) {
        throw new Error('action 为 update 时，resource 不可为空')
      }

      if (_.isEmpty(category)) {
        throw new Error('action 为 update 时，category 不可为空')
      }

      if (_.isEmpty(basicResource)) {
        throw new Error('action 为 update 时，basicResource 不可为空')
      }
    }
  }

  const actionUtils = {
    update: async() => {
      const targetResourcePath = basicResource.file_path_for_live_preview.split(',')[0]
      const newResourceUrl = resource.file_url
      const binPath = basicResource.bin_path[0]
      const playableBaseUrl = basicResource.basic_url

      const resourceUtils = {
        async image() {
          return await cookUpdateImageMessage({
            targetResourcePath,
            newResourceUrl,
            binPath,
            baseUrl,
            playableBaseUrl,
            visible
          })
        },

        audio() {
          if (
            typeof volume === 'undefined' &&
            typeof soundDisabled === 'undefined'
          ) {
            return cookUpdateAudioMessage({
              targetResourcePath,
              newResourceUrl
            })
          }
          return cookUpdateVolumeAudioMessage({
            targetResourcePath,
            newResourceUrl,
            volume,
            soundDisabled
          })
        },

        async zip() {
          if (!Array.isArray(basicResource.preview_urls_for_live_preview)) {
            console.warn('当前资源的 preview_urls 不是数组')
            return null
          }

          if (isFrameSequence(category.prefix, category.resource_type)) {
            return await cookUpdateFrameSequenceMessage({
              targetResourcePaths:
              basicResource.preview_urls_for_live_preview,
              newResourceUrls: resource.preview_urls,
              baseUrl,
              playableBaseUrl,
              filePath: basicResource.file_path,
              visible
            })
          }

          if (isDragonBones(category.prefix)) {
            return await cookUpdateDragonBonesMessage({
              targetResourcePath:
                path
                  .parse(basicResource.preview_urls_for_live_preview[0])
                  .name.split('_')[0] + '_tex.bin',
              newResourceUrls: resource.preview_urls,
              baseUrl,
              visible
            })
          }

          if (basicResource.prefix === 'lang') {
            // 语言包派发多次事件给实时预览脚本
            return basicResource.preview_urls_for_live_preview.map(
              (item, index) => {
                return cookUpdateImageMessage({
                  targetResourcePath: new Url(item).pathname,
                  newResourceUrl: resource.preview_urls[index],
                  baseUrl
                })
              }
            )
          }

          return null
        },

        // 视频返回空（不会发送消息给 iframe）
        video() {
          return null
        }
      }

      const cookMessage = async type => {
        return resourceUtils[type]()
      }

      const message = await cookMessage(category.file_type)

      // 返回的信息如果是数组，则派发多个事件
      if (Array.isArray(message)) {
        message.forEach(item => {
          dispatch('postMessage', item)
        })
      } else {
        dispatch('postMessage', message)
      }
    },

    navigate: () => {
      dispatch('previewActiveScene', scene)
    },

    refresh: () => {
      // 刷新直接跳转到 start 场景
      dispatch('previewActiveScene', 'start')
    }
  }

  actionUtils[action] && (await actionUtils[action]())
}
